import React, { useEffect, useMemo, useState } from 'react';
import { bool, arrayOf, oneOfType } from 'prop-types';
import { compose } from 'redux';
import { connect } from 'react-redux';
import classNames from 'classnames';

import { useConfiguration } from '../../context/configurationContext';
import { FormattedMessage, useIntl } from '../../util/reactIntl';
import {
  SCHEMA_TYPE_MULTI_ENUM,
  SCHEMA_TYPE_TEXT,
  SCHEMA_TYPE_YOUTUBE,
  propTypes,
} from '../../util/types';
import {
  NO_ACCESS_PAGE_USER_PENDING_APPROVAL,
  NO_ACCESS_PAGE_VIEW_LISTINGS,
  PROFILE_PAGE_PENDING_APPROVAL_VARIANT,
} from '../../util/urlHelpers';
import {
  isErrorNoViewingPermission,
  isErrorUserPendingApproval,
  isForbiddenError,
  isNotFoundError,
} from '../../util/errors';
import { pickCustomFieldProps } from '../../util/fieldHelpers';
import { hasPermissionToViewData, isUserAuthorized } from '../../util/userHelpers';
import { richText } from '../../util/richText';
import { isTrailerRenter } from '../../util/genericHelpers';
import { parseDateValue, parseGoogleReviewDate } from '../../util/reviewHelpers';

import { isScrollingDisabled } from '../../ducks/ui.duck';
import { getMarketplaceEntities } from '../../ducks/marketplaceData.duck';
import {
  H2,
  H4,
  Page,
  AvatarLarge,
  NamedLink,
  ListingCard,
  InlineTextButton,
  ReviewCard,
  ReviewRating,
  ExternalLink,
  LayoutSideNavigation,
  NamedRedirect,
} from '../../components';

import TopbarContainer from '../../containers/TopbarContainer/TopbarContainer';
import FooterContainer from '../../containers/FooterContainer/FooterContainer';
import NotFoundPage from '../../containers/NotFoundPage/NotFoundPage';

import css from './ProfilePage.module.css';
import SectionDetailsMaybe from './SectionDetailsMaybe';
import SectionTextMaybe from './SectionTextMaybe';
import SectionMultiEnumMaybe from './SectionMultiEnumMaybe';
import SectionYoutubeVideoMaybe from './SectionYoutubeVideoMaybe';
import { getAuthorSubscriptionData } from '../../util/subscriptionHelpers';
import { SUBSCRIPTION_PLAN_NAMES } from '../../util/enums';
import Icons from '../../components/Icons/Icons';
import { getSocialIconComponent } from '../ListingPage/ListingPageCarousel';

const MIN_LENGTH_FOR_LONG_WORDS = 20;
const REVIEWS_PREVIEW_LIMIT = 6;

export const AsideContent = props => {
  const { user, displayName, showLinkToProfileSettingsPage } = props;
  return (
    <div className={css.asideContent}>
      <AvatarLarge className={css.avatar} user={user} disableProfileLink />
      <H2 as="h1" className={css.mobileHeading}>
        {displayName ? (
          <FormattedMessage id="ProfilePage.mobileHeading" values={{ name: displayName }} />
        ) : null}
      </H2>
      {showLinkToProfileSettingsPage ? (
        <>
          <NamedLink className={css.editLinkMobile} name="ProfileSettingsPage">
            <FormattedMessage id="ProfilePage.editProfileLinkMobile" />
          </NamedLink>
          <NamedLink className={css.editLinkDesktop} name="ProfileSettingsPage">
            <FormattedMessage id="ProfilePage.editProfileLinkDesktop" />
          </NamedLink>
        </>
      ) : null}
    </div>
  );
};

export const ReviewsErrorMaybe = props => {
  const { queryReviewsError } = props;
  return queryReviewsError ? (
    <p className={css.error}>
      <FormattedMessage id="ProfilePage.loadingReviewsFailed" />
    </p>
  ) : null;
};

export const CombinedReviews = props => {
  const { reviews, googleReviews, queryReviewsError } = props;
  const [showAll, setShowAll] = useState(false);

  const combined = useMemo(() => {
    const googleItems = (googleReviews || []).map((review, index) => {
      const date = parseGoogleReviewDate(review?.date);
      const timestamp = date ? date.getTime() : 0;
      return {
        id: review?.id || `google-${index}`,
        source: 'google',
        authorName: review?.author,
        authorImage: review?.authorImage,
        rating: review?.rating,
        text: review?.text,
        date,
        timestamp,
      };
    });

    const platformItems = (reviews || []).map((review, index) => {
      const date = parseDateValue(review?.attributes?.createdAt);
      const timestamp = date ? date.getTime() : 0;
      return {
        id: review?.id?.uuid || `platform-${index}`,
        source: 'platform',
        author: review?.author,
        rating: review?.attributes?.rating,
        text: review?.attributes?.content,
        date,
        timestamp,
      };
    });

    return [...googleItems, ...platformItems].sort((a, b) => {
      if (a.timestamp === b.timestamp) {
        return 0;
      }
      return b.timestamp - a.timestamp;
    });
  }, [googleReviews, reviews]);

  const visibleReviews = showAll ? combined : combined.slice(0, REVIEWS_PREVIEW_LIMIT);
  const hasMore = combined.length > REVIEWS_PREVIEW_LIMIT;

  return (
    <section className={css.reviewsSection}>
      <div className={css.reviewsHeader}>
        <H4 as="h2" className={css.reviewsTitle}>
          <FormattedMessage id="ProfilePage.reviewsTitle" values={{ count: combined.length }} />
        </H4>
      </div>

      <ReviewsErrorMaybe queryReviewsError={queryReviewsError} />

      {combined.length === 0 ? (
        <p className={css.reviewsEmpty}>
          <FormattedMessage id="ProfilePage.reviewsEmpty" />
        </p>
      ) : (
        <>
          <ul className={css.reviewsList}>
            {visibleReviews.map(review => (
              <li key={review.id} className={css.reviewItem}>
                <ReviewCard
                  author={review.author}
                  authorName={review.authorName}
                  authorImage={review.authorImage}
                  rating={review.rating}
                  text={review.text}
                  date={review.date}
                  source={review.source}
                />
              </li>
            ))}
          </ul>
          {hasMore ? (
            <div className={css.reviewsActions}>
              <InlineTextButton
                className={css.showMoreButton}
                type="button"
                onClick={() => setShowAll(prev => !prev)}
              >
                <FormattedMessage
                  id={showAll ? 'ProfilePage.reviewsShowLess' : 'ProfilePage.reviewsShowMore'}
                />
              </InlineTextButton>
            </div>
          ) : null}
        </>
      )}
    </section>
  );
};

const SectionSocialLinksMaybe = ({ socialFeeds, subscriptionProductData }) => {
  const isAllowed =
    subscriptionProductData?.subscriptionProductName !== SUBSCRIPTION_PLAN_NAMES?.HAUL_STAR;

  if (!isAllowed) return null;

  if (!Array.isArray(socialFeeds) || socialFeeds.length === 0) return null;

  const validFeeds = socialFeeds.filter(feed => {
    if (!feed) return false;
    const platform = feed.platform || feed.name || '';
    return !!platform;
  });

  if (validFeeds.length === 0) return null;

  return (
    <div className={css.socialMediaWrap}>
      <h4 className={css.socialMediaHeader}>Social Media</h4>

      <div className={css.socialIcons}>
        {validFeeds.map((feed, index) => {
          if (!feed) return null;

          const platform = feed.platform || feed.name || '';
          const url = (feed.url || feed.link || '').trim();
          const IconComponent = getSocialIconComponent(platform);
          const key = `${platform || 'social'}-${index}`;

          if (!url) {
            return (
              <span key={key} className={css.socialIcon}>
                <IconComponent />
              </span>
            );
          }

          return (
            <a
              key={key}
              href={url}
              target="_blank"
              rel="noopener noreferrer"
              className={css.socialIconLink}
              aria-label={platform}
              title={platform}
            >
              <IconComponent />
            </a>
          );
        })}
      </div>
    </div>
  );
};

export const CustomUserFields = props => {
  const {
    publicData,
    metadata,
    userFieldConfig,
    authorSubscriptionData,
    subscriptionProductData,
  } = props;

  const shouldPickUserField = fieldConfig => fieldConfig?.showConfig?.displayInProfile !== false;

  let propsForCustomFields =
    pickCustomFieldProps(publicData, metadata, userFieldConfig, 'userType', shouldPickUserField) ||
    [];

  const noSubscription = !authorSubscriptionData?.subscriptionProductId;

  if (noSubscription) {
    propsForCustomFields = propsForCustomFields.filter(
      f => f.key !== 'phone_number' && f.key !== 'website'
    );
  }

  const socialFeeds = publicData?.socialFeeds || [];

  return (
    <>
      <SectionDetailsMaybe {...props} />

      {propsForCustomFields.map(customFieldProps => {
        const { schemaType, ...fieldProps } = customFieldProps;

        return schemaType === SCHEMA_TYPE_MULTI_ENUM ? (
          <SectionMultiEnumMaybe {...fieldProps} />
        ) : schemaType === SCHEMA_TYPE_TEXT ? (
          <SectionTextMaybe {...fieldProps} />
        ) : schemaType === SCHEMA_TYPE_YOUTUBE ? (
          <SectionYoutubeVideoMaybe {...fieldProps} />
        ) : null;
      })}

      <SectionSocialLinksMaybe
        socialFeeds={socialFeeds}
        subscriptionProductData={subscriptionProductData}
      />
    </>
  );
};

export const MainContent = props => {
  const {
    userShowError,
    bio,
    displayName,
    listings,
    queryListingsError,
    reviews,
    queryReviewsError,
    publicData,
    metadata,
    userFieldConfig,
    intl,
    hideReviews,
    userDetails,
  } = props;

  const hasListings = listings.length > 0;

  const hasBio = !!bio;
  const bioWithLinks = richText(bio, {
    linkify: true,
    longWordMinLength: MIN_LENGTH_FOR_LONG_WORDS,
    longWordClass: css.longWord,
  });

  const authorSubscriptionData = getAuthorSubscriptionData(userDetails);

  const listingsContainerClasses = classNames(css.listingsContainer, {
    [css.withBioMissingAbove]: !hasBio,
  });

  // Prominent Google rating block (V2). Reads the aggregate the owner imported.
  // Shown near the top of the profile as a trust signal. Suppressed when the
  // whole reviews area is hidden (e.g. renter profiles). No AggregateRating
  // schema is emitted anywhere (policy-safe: 3rd-party Google Maps rating shown
  // visibly on-page only).
  const gr = metadata?.googleReviews || null;
  const grAvg = Number(gr?.averageRating) || 0;
  const grTotal = Number(gr?.total) || 0;
  const grSourceUrl =
    typeof gr?.sourceUrl === 'string' && /^https:\/\//i.test(gr.sourceUrl) ? gr.sourceUrl : null;
  const grBusiness = gr?.businessName || null;
  const showGoogleRating = !hideReviews && grAvg > 0 && grTotal > 0;

  const googleRatingSummary = (
    <span className={css.googleRatingSummary}>
      <ReviewRating
        rating={grAvg}
        className={css.googleRatingStars}
        reviewStarClassName={css.googleRatingStar}
      />
      <span className={css.googleRatingText}>
        <FormattedMessage
          id="ProfilePage.googleRatingSummary"
          defaultMessage="{rating} · {count} Google reviews"
          values={{ rating: grAvg, count: grTotal }}
        />
      </span>
    </span>
  );

  const googleRatingBlock = showGoogleRating ? (
    <div className={css.googleRatingBlock}>
      {grSourceUrl ? (
        <ExternalLink
          href={grSourceUrl}
          className={css.googleRatingLink}
          title={grBusiness ? `View ${grBusiness} on Google` : 'View on Google'}
        >
          {googleRatingSummary}
        </ExternalLink>
      ) : (
        googleRatingSummary
      )}
      {grBusiness ? <span className={css.googleRatingBusiness}>{grBusiness}</span> : null}
    </div>
  ) : null;

  if (userShowError || queryListingsError) {
    return (
      <p className={css.error}>
        <FormattedMessage id="ProfilePage.loadingDataFailed" />
      </p>
    );
  }
  return (
    <div>
      <H2 as="h1" className={css.desktopHeading}>
        <FormattedMessage id="ProfilePage.desktopHeading" values={{ name: displayName }} />
        {authorSubscriptionData?.subscriptionProductName === SUBSCRIPTION_PLAN_NAMES?.HAUL_STAR && (
          <Icons name="goldBadge" />
        )}
        {authorSubscriptionData?.subscriptionProductName === SUBSCRIPTION_PLAN_NAMES?.BIG_RIG && (
          <Icons name="silverBadge" />
        )}
      </H2>
      {googleRatingBlock}
      {hasBio ? <p className={css.bio}>{bioWithLinks}</p> : null}

      {displayName ? (
        <CustomUserFields
          publicData={publicData}
          metadata={metadata}
          userFieldConfig={userFieldConfig}
          intl={intl}
          authorSubscriptionData={authorSubscriptionData}
        />
      ) : null}

      {hasListings ? (
        <div className={listingsContainerClasses}>
          <H4 as="h2" className={css.listingsTitle}>
            <FormattedMessage id="ProfilePage.listingsTitle" values={{ count: listings.length }} />
          </H4>
          <ul className={css.listings}>
            {listings.map(l => (
              <li className={css.listing} key={l.id.uuid}>
                <ListingCard listing={l} showAuthorInfo={false} />
              </li>
            ))}
          </ul>
        </div>
      ) : null}
      {hideReviews ? null : (
        <CombinedReviews
          reviews={reviews}
          googleReviews={metadata?.googleReviews?.reviews || []}
          queryReviewsError={queryReviewsError}
        />
      )}
    </div>
  );
};

export const ProfilePageComponent = props => {
  const config = useConfiguration();
  const intl = useIntl();
  const [mounted, setMounted] = useState(false);

  useEffect(() => {
    setMounted(true);
  }, []);

  const {
    scrollingDisabled,
    params: pathParams,
    currentUser,
    useCurrentUser,
    userShowError,
    user,
    userDetails,
    ...rest
  } = props;
  const isVariant = pathParams.variant?.length > 0;
  const isPreview = isVariant && pathParams.variant === PROFILE_PAGE_PENDING_APPROVAL_VARIANT;

  // Stripe's onboarding needs a business URL for each seller, but the profile page can be
  // too empty for the provider at the time they are creating their first listing.
  // To remedy the situation, we redirect Stripe's crawler to the landing page of the marketplace.
  // TODO: When there's more content on the profile page, we should consider by-passing this redirection.
  const searchParams = rest?.location?.search;
  const isStorefront = searchParams
    ? new URLSearchParams(searchParams)?.get('mode') === 'storefront'
    : false;
  if (isStorefront) {
    return <NamedRedirect name="LandingPage" />;
  }

  const isCurrentUser = currentUser?.id && currentUser?.id?.uuid === pathParams.id;
  const profileUser = useCurrentUser ? currentUser : user;
  const { bio, displayName, publicData, metadata } = profileUser?.attributes?.profile || {};
  const { userFields } = config.user;
  const isPrivateMarketplace = config.accessControl.marketplace.private === true;
  const isUnauthorizedUser = currentUser && !isUserAuthorized(currentUser);
  const isUnauthorizedOnPrivateMarketplace = isPrivateMarketplace && isUnauthorizedUser;
  const hasUserPendingApprovalError = isErrorUserPendingApproval(userShowError);
  const hasNoViewingRightsUser = currentUser && !hasPermissionToViewData(currentUser);
  const hasNoViewingRightsOnPrivateMarketplace = isPrivateMarketplace && hasNoViewingRightsUser;

  const isDataLoaded = isPreview
    ? currentUser != null || userShowError != null
    : hasNoViewingRightsOnPrivateMarketplace
    ? currentUser != null || userShowError != null
    : user != null || userShowError != null;

  const schemaTitleVars = { name: displayName, marketplaceName: config.marketplaceName };
  const schemaTitle = intl.formatMessage({ id: 'ProfilePage.schemaTitle' }, schemaTitleVars);

  if (!isDataLoaded) {
    return null;
  } else if (!isPreview && isNotFoundError(userShowError)) {
    return <NotFoundPage staticContext={props.staticContext} />;
  } else if (!isPreview && (isUnauthorizedOnPrivateMarketplace || hasUserPendingApprovalError)) {
    return (
      <NamedRedirect
        name="NoAccessPage"
        params={{ missingAccessRight: NO_ACCESS_PAGE_USER_PENDING_APPROVAL }}
      />
    );
  } else if (
    (!isPreview && hasNoViewingRightsOnPrivateMarketplace && !isCurrentUser) ||
    isErrorNoViewingPermission(userShowError)
  ) {
    // Someone without viewing rights on a private marketplace is trying to
    // view a profile page that is not their own – redirect to NoAccessPage
    return (
      <NamedRedirect
        name="NoAccessPage"
        params={{ missingAccessRight: NO_ACCESS_PAGE_VIEW_LISTINGS }}
      />
    );
  } else if (!isPreview && isForbiddenError(userShowError)) {
    // This can happen if private marketplace mode is active, but it's not reflected through asset yet.
    return (
      <NamedRedirect
        name="SignupPage"
        state={{ from: `${location.pathname}${location.search}${location.hash}` }}
      />
    );
  } else if (isPreview && mounted && !isCurrentUser) {
    // Someone is manipulating the URL, redirect to current user's profile page.
    return isCurrentUser === false ? (
      <NamedRedirect name="ProfilePage" params={{ id: currentUser?.id?.uuid }} />
    ) : null;
  } else if ((isPreview || isPrivateMarketplace) && !mounted) {
    // This preview of the profile page is not rendered on server-side
    // and the first pass on client-side should render the same UI.
    return null;
  }
  // Google's ProfilePage structured-data spec REQUIRES a mainEntity (the
  // Person/Organization the profile is about) — without it Search Console
  // flags every profile page "Missing field mainEntity" and the pages are
  // ineligible for profile rich results. Build it from the loaded user.
  // Owners are typically businesses (Organization); renters are people.
  const profileUserId = profileUser?.id?.uuid || pathParams.id;
  const profileUrl =
    config.marketplaceRootURL && profileUserId
      ? `${config.marketplaceRootURL.replace(/\/$/, '')}/u/${profileUserId}`
      : null;
  const profileImageUrl =
    profileUser?.profileImage?.attributes?.variants?.['square-small2x']?.url ||
    profileUser?.profileImage?.attributes?.variants?.['square-small']?.url ||
    null;
  const isOwnerProfile = (publicData?.userType || '').includes('owner');
  const schemaMainEntity = {
    '@type': isOwnerProfile ? 'Organization' : 'Person',
    name: displayName,
    ...(profileUserId ? { identifier: profileUserId } : {}),
    ...(profileUrl ? { url: profileUrl, '@id': profileUrl } : {}),
    ...(bio ? { description: bio } : {}),
    ...(profileImageUrl ? { image: profileImageUrl } : {}),
    ...(typeof publicData?.userLocation === 'string' && publicData.userLocation.trim()
      ? { address: { '@type': 'PostalAddress', addressRegion: publicData.userLocation.trim() } }
      : {}),
  };

  // This is rendering normal profile page (not preview for pending-approval)
  return (
    <Page
      scrollingDisabled={scrollingDisabled}
      title={schemaTitle}
      schema={{
        '@context': 'http://schema.org',
        '@type': 'ProfilePage',
        name: schemaTitle,
        ...(displayName ? { mainEntity: schemaMainEntity } : {}),
      }}
    >
      <LayoutSideNavigation
        sideNavClassName={css.aside}
        topbar={<TopbarContainer />}
        sideNav={
          <AsideContent
            user={profileUser}
            showLinkToProfileSettingsPage={mounted && isCurrentUser}
            displayName={displayName}
          />
        }
        footer={<FooterContainer />}
      >
        <MainContent
          bio={bio}
          displayName={displayName}
          userShowError={userShowError}
          publicData={publicData}
          metadata={metadata}
          userFieldConfig={userFields}
          hideReviews={hasNoViewingRightsOnPrivateMarketplace || isTrailerRenter(profileUser)}
          intl={intl}
          userDetails={userDetails}
          {...rest}
        />
      </LayoutSideNavigation>
    </Page>
  );
};

ProfilePageComponent.defaultProps = {
  currentUser: null,
  user: null,
  userShowError: null,
  queryListingsError: null,
  reviews: [],
  queryReviewsError: null,
};

ProfilePageComponent.propTypes = {
  scrollingDisabled: bool.isRequired,
  currentUser: propTypes.currentUser,
  useCurrentUser: bool.isRequired,
  user: oneOfType([propTypes.user, propTypes.currentUser]),
  userShowError: propTypes.error,
  queryListingsError: propTypes.error,
  listings: arrayOf(oneOfType([propTypes.listing, propTypes.ownListing])).isRequired,
  reviews: arrayOf(propTypes.review),
  queryReviewsError: propTypes.error,
};

const mapStateToProps = state => {
  const { currentUser, userDetails } = state.user;
  const {
    userId,
    userShowError,
    queryListingsError,
    userListingRefs,
    reviews,
    queryReviewsError,
  } = state.ProfilePage;
  const userMatches = getMarketplaceEntities(state, [{ type: 'user', id: userId }]);
  const user = userMatches.length === 1 ? userMatches[0] : null;

  // Show currentUser's data if it's not approved yet
  const isCurrentUser = userId?.uuid === currentUser?.id?.uuid;
  const useCurrentUser =
    isCurrentUser && !(isUserAuthorized(currentUser) && hasPermissionToViewData(currentUser));

  return {
    scrollingDisabled: isScrollingDisabled(state),
    currentUser,
    useCurrentUser,
    user,
    userShowError,
    queryListingsError,
    listings: getMarketplaceEntities(state, userListingRefs),
    reviews,
    queryReviewsError,
    userDetails,
  };
};

const ProfilePage = compose(connect(mapStateToProps))(ProfilePageComponent);

export default ProfilePage;
